import type { ApplogValue } from '../../applog/datom-types.ts'
import type { ISchemaAdapter, VMAttributeDef } from '../types.ts'

/**
 * Create a schema adapter from a TypeBox object schema.
 *
 * Usage:
 * ```ts
 * import { Type } from '@sinclair/typebox'
 * import { createTypeBoxAdapter } from '@wovin/core/viewmodel/adapters/typebox'
 *
 * const MySchema = Type.Object({
 *   name: Type.String(),
 *   age: Type.Optional(Type.Number()),
 * })
 *
 * const adapter = createTypeBoxAdapter(MySchema, 'myEntity')
 * ```
 *
 * The type parameter `T` is the inferred static type of the schema.
 * Can pass a compiled validator separately if runtime validation is needed.
 */
export function createTypeBoxAdapter<T extends Record<string, ApplogValue>>(
	typeBoxSchema: { properties: Record<string, any>; type?: string },
	entityPrefix: string,
	options?: {
		/** Override at-paths for specific attributes */
		atOverrides?: Record<string, string>
		/** Default values */
		defaults?: Partial<T>
		/** Property name to at-path mapping function */
		toAtPath?: (entityPrefix: string, attrName: string) => string
		/** A pre-compiled validator function (e.g. from TypeCompiler.Compile) */
		validator?: (value: unknown) => value is T
	},
): ISchemaAdapter<T> {
	const properties = typeBoxSchema.properties as Record<string, any>
	const toAtPath = options?.toAtPath ?? ((prefix, name) => `${prefix}/${name}`)

	const attributeDefs: VMAttributeDef[] = Object.entries(properties).map(([name, propSchema]: [string, any]) => {
		const isOptional = propSchema.optional ?? false
		const wovinAtName = propSchema.wovinAtName
		const atPath = options?.atOverrides?.[name] ?? wovinAtName ?? toAtPath(entityPrefix, name)

		return {
			name,
			atPath,
			optional: isOptional,
			defaultValue: propSchema.default,
		}
	})

	return {
		getAttributeDefs: () => attributeDefs,
		getDefaults: () => (options?.defaults ?? {}) as Partial<T>,
		getEntityPrefix: () => entityPrefix,
		createValidator: options?.validator ? () => options.validator! : undefined,
	}
}
